import Carousel from 'nuka-carousel';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { styled } from '@styledComponents';
import { CTALink } from '@components/CTALink';
import { Text } from '@components/typography';
import { ButtonNoneStyled } from '@components/ButtonNoneStyled';
import { themeColor, themeMediaQuery, themeSpace, themeVariant } from '@utils/styledTheme.util';

const MAX_WIDTH = 370;
const CAROUSEL_MAX_WIDTH = 1238;
const IMAGE_HEIGHT = 247;
const IMAGE_HEIGHT_MOBILE = 223;
const SLIDE_NAV_Z_INDEX = 99;
const SLIDE_NAV_TOP = 99.5;
const SLIDE_NAV_TOP_MOBILE = 87.5;

export const StyledText = styled(Text)`
  ${themeMediaQuery('below_sm')} {
    ${themeVariant('sectionEyeBrow24')};
  }
`;

export const CarouselWrapper = styled(Flex)`
  position: relative;
  max-width: ${CAROUSEL_MAX_WIDTH}px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 ${themeSpace(6)};
  .slider-list {
    cursor: default !important;
    height: 508px !important;
  }
  ${themeMediaQuery('sm')} {
    padding: 0 ${themeSpace(5)};
  }
`;

export const StyledCarousel = styled(Carousel)`
  .slider-slide :focus-visible {
    outline: none;
  }
  ${themeMediaQuery('tablet')} {
    .slider-slide {
      padding: 0 ${themeSpace(5)};
    }
  }
  ${themeMediaQuery('sm')} {
    .slider-slide {
      padding: 0 ${themeSpace(5)};
    }
  }
`;

export const CarouselGrid = styled(Flex)`
  ${themeMediaQuery('tablet')} {
    display: grid;
    gap: ${themeSpace(7)};
    grid-template-columns: repeat(2, minmax(0px, 1fr));
  }
  ${themeMediaQuery('sm')} {
    display: grid;
    gap: ${themeSpace(7)};
    grid-template-columns: repeat(3, minmax(0px, 1fr));
  }
`;

export const CarouselItem = styled(Flex)`
  position: relative;
  height: 100%;
  ${themeMediaQuery('tablet')} {
    max-width: ${MAX_WIDTH}px;
  }
  ${themeMediaQuery('sm')} {
    max-width: ${MAX_WIDTH}px;
  }
`;

export const ImageContainer = styled(Box)`
  position: relative;
  overflow: hidden;
  border-top-left-radius: ${themeSpace(3)};
  border-top-right-radius: ${themeSpace(3)};
`;

export const ImageCTALink = styled(CTALink)<{ backgroundImage: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: inline-block;
  vertical-align: top;
  background-color: ${themeColor('mono5')};
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  overflow: hidden;
  background-image: url(${({ backgroundImage }) => backgroundImage});

  &:before {
    display: block;
    content: '';
    width: 100%;
    padding-top: 65.41%;
    background: 0 0 !important;
    box-shadow: none !important;
  }

  height: ${IMAGE_HEIGHT}px;
  ${themeMediaQuery('below_sm')} {
    height: ${IMAGE_HEIGHT_MOBILE}px;
  }
`;

export const StyledEyeBrow = styled(CTALink)<{ isLast: boolean }>`
  font-size: 14px;
  line-height: 20px;
  position: relative;
  color: ${themeColor('actionPurple2')};

  margin-right: ${themeSpace(4)};
  padding-right: ${themeSpace(5)};

  &:after {
    content: '';
    display: ${({ isLast }) => (isLast ? 'none' : 'inline-block')};
    border: 1px solid;
    font-size: 12px;
    position: absolute;
    top: 50%;
    right: 0;
    margin-top: -3px;
    width: 3px;
    height: 3px;
    transform: rotate(45deg);
    -moz-transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
    box-sizing: unset;
    -webkit-box-sizing: unset;
    opacity: 0.65;
  }
`;

export const StyledTitle = styled(CTALink)`
  line-height: 29px;
  color: ${themeColor('neutra6')};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const StyledContent = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

export const StyledTextWrapper = styled(Flex)`
  gap: ${themeSpace(5)};
  border-bottom-left-radius: ${themeSpace(3)};
  border-bottom-right-radius: ${themeSpace(3)};
`;

export const SlidButton = styled(ButtonNoneStyled)`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  height: ${themeSpace(9)};
  width: ${themeSpace(9)};
  background-color: rgba(247, 247, 247, 0.8);
  box-shadow: 0px 7px 16px -8px rgba(0, 0, 0, 0.3), 0px 10px 27px -5px rgba(44, 41, 37, 0.25);

  top: ${SLIDE_NAV_TOP_MOBILE}px;
  z-index: ${SLIDE_NAV_Z_INDEX};
  position: absolute;

  &:hover svg {
    color: ${themeColor('neutra6')};
  }

  ${themeMediaQuery('sm')} {
    top: ${SLIDE_NAV_TOP}px;
  }
`;

export const PreviousSlideButton = styled(SlidButton)`
  left: ${themeSpace(4)};
  ${themeMediaQuery('sm')} {
    left: ${themeSpace(3)};
  }
`;

export const NextSlideButton = styled(SlidButton)`
  right: ${themeSpace(4)};
  ${themeMediaQuery('sm')} {
    right: ${themeSpace(3)};
  }
`;
